/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RootState } from "./store"
import { AccountDialogState, CLOSED } from "./types"

export interface PreferencesState {
    showPreferences: boolean
    showAccountSetUp: AccountDialogState
    navbarExpand: boolean
    sideBar: boolean
    showSearchFilters: boolean
    showDnoApplicationFilters: boolean
    showMFA: boolean
    theme: string
    showCompanyDetailsPref: string
}

export const DARK = "dark"
export const LIGHT = "light"

// per requirement #59549, popup should not show for 30 days
const NO_DAYS_IN_FUTURE = 30
const COMPANY_DETAILS_POPUP_KEY = "showCompanyDetailsPopup"

const sideBarPref = localStorage.getItem("SideBarPref") === "true"

const showCompanyDetailsPref = localStorage.getItem(COMPANY_DETAILS_POPUP_KEY)

const THEME_PREF_KEY = "ThemePref"
const storedTheme = localStorage.getItem(THEME_PREF_KEY)

const prefersDarkMode =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches

const getInitialTheme = (): string => {
    if (storedTheme === DARK || storedTheme === LIGHT) {
        return storedTheme
    }
    return prefersDarkMode ? DARK : LIGHT
}

const initialTheme = getInitialTheme()

// Define initial state
const initialState: PreferencesState = {
    showPreferences: false,
    showAccountSetUp: CLOSED,
    navbarExpand: true,
    sideBar: sideBarPref,
    showSearchFilters: false,
    showDnoApplicationFilters: false,
    showMFA: false,
    theme: initialTheme,
    showCompanyDetailsPref: showCompanyDetailsPref || "",
}

export const preferencesSlice = createSlice({
    name: "preferences",
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        setShowPreferences: (state, action: PayloadAction<boolean>) => {
            state.showPreferences = action.payload
        },
        setShowAccountSetUp: (
            state,
            action: PayloadAction<AccountDialogState>
        ) => {
            state.showAccountSetUp = action.payload
        },
        setNavBarExpand: (state, action: PayloadAction<boolean>) => {
            state.navbarExpand = action.payload
        },
        setSideBar: (state, action: PayloadAction<boolean>) => {
            state.sideBar = action.payload
            localStorage.setItem("SideBarPref", action.payload.toString())
        },
        setShowSearchFilters: (state) => {
            state.showSearchFilters = !state.showSearchFilters
        },
        setShowDnoApplicationFilters: (state) => {
            state.showDnoApplicationFilters = !state.showDnoApplicationFilters
        },
        setShowMFA: (state, action: PayloadAction<boolean>) => {
            state.showMFA = action.payload
        },
        setTheme: (state, action: PayloadAction<string>) => {
            state.theme = action.payload
            localStorage.setItem(THEME_PREF_KEY, action.payload)
        },
        setShowCompanyDetailsPref: (state) => {
            const futureDate = new Date()
            futureDate.setDate(futureDate.getDate() + NO_DAYS_IN_FUTURE)
            state.showCompanyDetailsPref = futureDate.toISOString()
            localStorage.setItem(
                COMPANY_DETAILS_POPUP_KEY,
                state.showCompanyDetailsPref.toString()
            )
        },
    },
})

export const {
    setShowPreferences,
    setShowAccountSetUp,
    setNavBarExpand,
    setSideBar,
    setShowSearchFilters,
    setShowMFA,
    setShowDnoApplicationFilters,
    setTheme,
    setShowCompanyDetailsPref,
} = preferencesSlice.actions

// Helper functions for selecting state
export const getShowPreferences = (state: RootState): boolean =>
    state.preferences.showPreferences
export const getShowAccountSetUp = (state: RootState): AccountDialogState =>
    state.preferences.showAccountSetUp
export const getNavBarExpand = (state: RootState): boolean =>
    state.preferences.navbarExpand
export const getSideBar = (state: RootState): boolean =>
    state.preferences.sideBar
export const getShowSearchFilters = (state: RootState): boolean =>
    state.preferences.showSearchFilters
export const getShowDnoApplicationFilters = (state: RootState): boolean =>
    state.preferences.showDnoApplicationFilters
export const getShowMFA = (state: RootState): boolean =>
    state.preferences.showMFA
export const getTheme = (state: RootState): string => state.preferences.theme
export const getShowCompanyDetailsPref = (state: RootState): boolean => {
    if (state.preferences.showCompanyDetailsPref === "") {
        return true
    }
    const storedDate = new Date(state.preferences.showCompanyDetailsPref)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    storedDate.setHours(0, 0, 0, 0)

    if (storedDate < today) {
        return true
    }

    return false
}

export default preferencesSlice.reducer
